import React, { useCallback, useContext, useMemo } from "react";
import { useStyles } from "../RCACompliance.styles";
import { IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ComplianceBar from "../../components/ComplianceBar";
import { RCAAPComplianceContext } from "contexts/RCAAPComplianceContext/RCAAPComplianceContext";
import moment from "moment";
import { RCAAPComplianceEntity } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntity";
import { ComplianceBarReferences } from "../../components/ComplianceBar/ComplianceBarReferences.view";
import { ComplianceBarPercentageReference } from "../../components/ComplianceBar/ComplianceBarPercentageReference.view";
import RCAAPComplianceByResponsibleFilter from "../../components/filters/RCAAPComplianceByResponsibleFilter/RCAAPComplianceByResponsibleFilter.view";
import { IRCAComplianceByResponsible } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntityInterfaces";
import { IRCAAPBarComplianceTypes, RCAAPComplianceByResponsibleFilterEnum } from "../../RCAAPComplianceInterfaces";
import { getComplianceByType, getRCAAPComplianceScoreByResponsible } from "utilities/utilities";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ComplianceTypes } from "constants/constants";

const RCAComplianceByResponsible = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters, handleSetTypeFilter, handleSetComplianceType } = useContext(RCAAPComplianceContext);
    const argsFilters = useMemo(
        () => ({
            date: moment().subtract(Number(filters.periodOption), "day").startOf("day").toISOString(),
            meetingTypeIds: filters.meetingTypeIds,
            totalMeetingTypes: filters.totalMeetingTypes,
        }),
        [filters],
    );
    const { rcaComplianceByResponsible } = RCAAPComplianceEntity.GetRCAComplianceByResponsible(argsFilters);

    const selectedComplianceTypeData = getComplianceByType(rcaComplianceByResponsible, filters.complianceType);

    const sortResponsibleData = useCallback((data: IRCAComplianceByResponsible[], order: string) => {
        let sortedData: IRCAComplianceByResponsible[] = [];
        const dataWithScores = data.map((responsible) => {
            return {
                ...responsible,
                score: getRCAAPComplianceScoreByResponsible(responsible),
            };
        });

        if (order === RCAAPComplianceByResponsibleFilterEnum.high_to_low) {
            sortedData = dataWithScores.sort((a, b) => b.score - a.score);
        }

        if (order === RCAAPComplianceByResponsibleFilterEnum.low_to_high) {
            sortedData = dataWithScores.sort((a, b) => a.score - b.score);
        }

        return sortedData;
    }, []);

    const sortingTypes = useCallback(
        (data: IRCAComplianceByResponsible[], criteria: string) => {
            switch (criteria) {
                case RCAAPComplianceByResponsibleFilterEnum.a_to_z_asc:
                    return data.sort((a, b) => a.user.first_name.localeCompare(b.user.first_name));
                case RCAAPComplianceByResponsibleFilterEnum.a_to_z_desc:
                    return data.sort((a, b) => b.user.first_name.localeCompare(a.user.first_name));
                case RCAAPComplianceByResponsibleFilterEnum.low_to_high:
                    return sortResponsibleData(data, RCAAPComplianceByResponsibleFilterEnum.low_to_high);
                case RCAAPComplianceByResponsibleFilterEnum.high_to_low:
                    return sortResponsibleData(data, RCAAPComplianceByResponsibleFilterEnum.high_to_low);
                default:
                    return data.sort((a, b) => a.user.first_name.localeCompare(b.user.first_name));
            }
        },
        [sortResponsibleData],
    );

    const barsData = useMemo(() => {
        const sortedData = sortingTypes(
            selectedComplianceTypeData as IRCAComplianceByResponsible[],
            filters.typeOption,
        );

        return sortedData.map((rca) => {
            const summarizeTotalWhenSelectedComplianceType =
                filters.complianceType !== ComplianceTypes.NONE
                    ? rca.completed_on_time + rca.pending + rca.completed_late + rca.pending_late
                    : rca.total_rcas_by_responsible;
            return [
                {
                    responsible: {
                        name: `${rca.user.first_name} ${rca.user.last_name}`,
                        id: rca.user.user_id,
                        media: rca.user.media?.media_url,
                    },
                    completedOnTimeAmount: rca.completed_on_time,
                    pendingAmount: rca.pending,
                    completedLateAmount: rca.completed_late,
                    pendingLateAmount: rca.pending_late,
                    total: summarizeTotalWhenSelectedComplianceType,
                },
            ];
        });
    }, [selectedComplianceTypeData, filters, sortingTypes]);

    return (
        <div className={classes.card}>
            <div className={classes.cardHeader}>
                <div className={classes.cartTitleContainer}>
                    <Typography variant="rcaTitle" className={classes.cardTitle}>
                        {t("rca.compliance.rca_compliance.by_responsible.title")}
                    </Typography>
                    <IconButton onClick={() => handleSetComplianceType(ComplianceTypes.NONE)}>
                        <RefreshIcon />
                    </IconButton>
                </div>
                <RCAAPComplianceByResponsibleFilter filters={filters} setFilters={handleSetTypeFilter} />
            </div>
            <div className={classes.cardBody}>
                {barsData.length === 0 ? (
                    <ComplianceBar />
                ) : (
                    <>
                        <ComplianceBarPercentageReference />
                        {barsData
                            .filter((barData) =>
                                Object.keys(barData[0]).some(
                                    (key) =>
                                        (Object.values(IRCAAPBarComplianceTypes) as string[]).includes(key) &&
                                        barData[0][key as IRCAAPBarComplianceTypes] > 0,
                                ),
                            )
                            .map((barData, index) => (
                                <ComplianceBar
                                    hidePercentage={filters.complianceType !== ComplianceTypes.NONE}
                                    data={barData}
                                    key={index}
                                    withBottomBorder={true}
                                />
                            ))}
                        {barsData.length > 0 && <ComplianceBarReferences />}
                    </>
                )}
            </div>
        </div>
    );
};

export default RCAComplianceByResponsible;
